/**
 * Analytics Page
 * This page is intended to empower the customer to uncover
 * insights and trends in their data, over longer periods of time.
 */
import { useState, useEffect, } from 'react';
import { useData } from '../../hooks/useData';
import type { DayPartData } from '../../types/DayPartData';
import type { RegionsStats } from '../../types/RegionsStats';
import type { Average } from '../../types/Average';
import {
  getDayPartsData,
  getRegionsStats,
  getAverages,
  getGroupRegionsStats,
  getGroupsAverages,
  getGroupDayPartsData,
} from '../../utils/api';
import DropdownFranchise from '../../components/dropdowns/DropdownFranchise';
import DropdownLocationMultiple from '../../components/dropdowns/DropdownLocationMultiple';
import DatepickerWithDefaults from '../../components/time/DatepickerWIthDefaults';
import DriveThruLobbyToggle from '../../components/buttons/DriveThruLobbyToggle';
import HelpButton from '../../components/buttons/HelpButton';
import HelpModal from '../../components/modals/ModalHelpAnalytics';
import TrendsScatter from '../../components/graphs/analytics/TrendsScatter';
import StationsGraph from '../../components/graphs/analytics/StationsGraph';
import TrendsGraph from '../../components/graphs/analytics/TrendsGraph';
import DayPartsGraph from '../../components/graphs/analytics/DayPartsGraph';
import PageLayout from '../../components/ui/PageLayout';
import GroupsButton from '@/components/buttons/GroupsButton';
import ModalBasic from '@/components/modals/ModalBasic';
import GroupModal from '@/components/modals/GroupModal';
import { type GroupResponse } from '@/types/GroupResponse';
import { getAllGroups, getGroupLocationsByName } from '@/utils/api';
import HellometerLoading from './HellometerLoading';

export default function AnalyticsContainer({
  version = null,
}: {
  version?: string | null;
}): JSX.Element {
  const {
    selectedFranchise,
    selectedLocations,
    selectedDates,
    handleSelectedDates,
    toggled,
    handleSelectGroups,
    cachedGroups,
    loadingProfile

  } = useData();

  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [dayPartsData, setDayPartsData] = useState<DayPartData[]>([]);
  const [regionsData, setRegionsData] = useState<RegionsStats[]>([]);
  const [trendsData, setTrendsData] = useState<Average[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupResponse[]>([]);
  // const [selectedGroups, setSelectedGroups] = useState<GroupResponse[]>(cachedGroups);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fetchGroupData, setFetchGroupData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const franchiseId = selectedFranchise?.id || '';
    if (cachedGroups.length === 0 || !franchiseId) {
      return;
    }
    // Fetch group locations
    Promise.all(
      cachedGroups.map(async (group) => await getGroupLocationsByName(group, franchiseId))
    ).then((responses) => {
      // Flatten all groupLocations arrays (which are arrays of location IDs)
      const allLocationIds = responses.flat();
      // Remove duplicates
      const uniqueLocationIds = Array.from(new Set(allLocationIds));
      // Map IDs to location objects from selectedLocations (or another source if needed)
      handleSelectGroups(cachedGroups, uniqueLocationIds, '');
    }).catch((error) => {
      console.error(error);
    });
  }, [cachedGroups]);

  useEffect(() => {
    const franchiseId = selectedFranchise?.id || '';
    if (!franchiseId || !selectedFranchise) {
      return;
    }
     const aweekago = new Date();
     const yesterday = new Date();
      yesterday.setDate(new Date().getDate() - 1)
     aweekago.setDate(new Date().getDate() - 7)
    handleDateChange([aweekago, yesterday]);

    getAllGroups(franchiseId).then((res) => {
      setGroupOptions(res);
    });
  }, [selectedFranchise]);

  // Get day parts data when toggled or date or location or franchise changes.
  useEffect(() => {
    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    async function getData(): Promise<void> {
      if (selectedFranchise === null || selectedLocations.length === 0) {
        return;
      }

      // Get day parts data.
      setIsLoading(true);
      // Get trends data.
      if (!fetchGroupData) {
        // averages
        getAverages(
        selectedFranchise.id,
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        selectedLocations.map((location) => location.id),
        toggled,
        0, // Interval for rolling average - set at 0 until dynamic setting is needed
        { signal: newAbortController.signal } // pass the abort signal
      )
        .then((data) => {
          setTrendsData(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
        // Get regions data.
      getRegionsStats(
        selectedFranchise.id,
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        selectedLocations.map((location) => location.id),
        toggled,
        { signal: newAbortController.signal } // pass the abort signal
      )
        .then((data) => {
          setRegionsData(data);
        })
        .catch((error) => {
          console.error(error);
        });

        // dayparts
        getDayPartsData(
        selectedFranchise.id,
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        selectedLocations.map((location) => location.id),
        toggled,
        { signal: newAbortController.signal } // pass the abort signal
      )
        .then((data) => {
          setDayPartsData(data);
        })
        .catch((error) => {
          console.error(error);
        });
      } else if (cachedGroups.length > 0) {
        getGroupsAverages(
        selectedFranchise.id,
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        cachedGroups.map((group) => group.groupId),
        toggled,
        { signal: newAbortController.signal } // pass the abort signal
      )
        .then((data) => {
          setTrendsData(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });

        getGroupRegionsStats(
          selectedFranchise.id,
          selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        cachedGroups.map((group) => group.groupId),
        toggled,
        { signal: newAbortController.signal } // pass the abort signal
        ).then((data) => {
          setRegionsData(data);
        }).catch((error) => {
          console.error(error);
        });

        getGroupDayPartsData(
        selectedFranchise.id,
        selectedDates[0],
        selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
        cachedGroups.map((group) => group.groupId),
        toggled,
        { signal: newAbortController.signal } // pass the abort signal
      )
        .then((data) => {
          setDayPartsData(data);
        })
        .catch((error) => {
          console.error(error);
        });
      }
    }
    getData().catch((error) => {
      console.error(error);
    });
  }, [toggled, selectedDates, selectedLocations, selectedFranchise, fetchGroupData]);

  // Handle date change
  function handleDateChange(dates: Date[]): void {
    handleSelectedDates(dates);
  }

  const handleRefreshGroups = () => {
    if (selectedFranchise) {
      getAllGroups(selectedFranchise.id).then((res) => {
        setGroupOptions(res);
      });
    }
  };

  return (
    <PageLayout>
      <div data-cy="analytics">
        {/* Page header */}
        <div className="sm:flex sm:justify-between sm:items-center mb-8">
          {/* Left: Title */}
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
              Analytics
            </h1>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Selectors */}
          <div className="sm:flex sm:justify-between sm:items-center mb-8 gap-4">
            <DropdownFranchise />
            <DropdownLocationMultiple disabled={fetchGroupData} />
                {/* Group Filters -- hide if no group options */}
                {groupOptions.length === 0
? (
                  <></>
                )
: (
                  <>
                    <GroupsButton
                      onClick={() => {
                        setIsOpen((prev) => !prev);
                      }}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs ${fetchGroupData ? 'text-slate-500' : 'text-slate-400'}`}>
                        Individuals
                      </span>
                        <div className="relative group">
                          <button
                            id="fetch-group-data-switch"
                            type="button"
                            disabled={cachedGroups.length === 0}
                            aria-pressed={fetchGroupData}
                            onClick={() => { setFetchGroupData((prev) => !prev); }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                              ${fetchGroupData ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}
                              ${cachedGroups.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            data-cy="fetch-group-data-switch"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${fetchGroupData ? 'translate-x-5' : 'translate-x-1'}
                              `}
                            />
                          </button>
                          {cachedGroups.length === 0 && (
                            <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-max -translate-x-1/2 rounded bg-slate-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                              Select a group first
                            </div>
                          )}
                        </div>
                        <span className={`text-xs ${fetchGroupData ? 'text-slate-400' : 'text-slate-500'}`}>
                        Group
                        </span>
                    </div>
                    <div hidden={true}>
                      <ModalBasic
                        title="Groups"
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        dataCy="modal-filter-groups"
                        full_h={true}
                      >
                        <GroupModal
                          groupOptions={groupOptions}
                          refreshGroups={handleRefreshGroups}
                        />
                      </ModalBasic>
                    </div>

                    </>
                )}

            <DatepickerWithDefaults
              initDates={selectedDates}
              handleDateChange={handleDateChange}
              mode="range"
            />
            <DriveThruLobbyToggle />
            <HelpButton
              onClick={() => {
                setShowHelpModal(true);
              }}
            />
          </div>
          {/* Graphs */}
          {(selectedLocations.length === 0 || selectedFranchise === null || loadingProfile)
                      ? <HellometerLoading/>
                      : <>
                      {version === 'v2'
? (
            
            <TrendsScatter
              isLoading={isLoading}
              trendsData={trendsData}
              dayPartsData={dayPartsData}
              regionsData={regionsData}
              groupData={fetchGroupData}
            />
          )
: (
            <>
              <TrendsGraph trendsData={trendsData} />
              <StationsGraph regionsData={regionsData} />
              <DayPartsGraph dayPartsData={dayPartsData} />
            </>
          )}</>}

          
          
        </div>
        {/* Help modal */}
        <HelpModal isOpen={showHelpModal} setIsOpen={setShowHelpModal} />
      </div>
    </PageLayout>
  );
}
